import Vue from 'vue';
import { VueConstructor } from 'vue/types/vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

import { OrderWithContext, FormField } from '@/models';
import CmgPayeezyForm from '../../payment/CmgPayeezyForm/CmgPayeezyForm.vue';
import CmgChaseForm from '../../payment/CmgChaseForm/CmgChaseForm.vue';
import CmgChaseUkForm from '../../payment/CmgChaseUkForm/CmgChaseUkForm.vue';
import CmgUComForm from '../../payment/CmgUComForm/CmgUComForm.vue';
import { PaymentProviders } from '@/constants';
import { CheckoutEvent } from '@/services/analytics/events';

@Component({
  name: 'CmgCheckoutOneTimePayment',
  components: {
    CmgPayeezyForm,
    CmgChaseForm,
    CmgUComForm,
    CmgChaseUkForm
  },
})
export default class CmgCheckoutOneTimePayment extends Vue {
  sentCheckoutEvent = false;
  providerId = '';
  paymentComponent: any = null;
  componentKey: number = 0;

  @Prop({ default: false, type: Boolean })
  showRememberPaymentMethod!: boolean;

  @Prop({ default: false, type: Boolean })
  isProcessingError!: boolean;

  @Prop({ default: '', type: String })
  errorText!: string;

  @Watch('isProcessingError', { immediate: true })
  onProcessingErrorChanged(newProcessingError: boolean) {
    // During new delivery error handling scenarios, the effort is to provide better clarity to the user, and to prevent accidental charges
    // To wipe out the user's info in the payeezy / chase form, we will reset the component key that is passed to the relevant component
    // This forces a replacement of the component instead of reusing it, which will wipe all customer data from the form
    this.componentKey = Date.now() + Math.random();
  }

  get rememberPaymentMethod(): FormField<boolean> {
    return this.$appStore.state.checkout.payment.rememberPaymentMethod;
  }

  get order(): OrderWithContext | null {
    return this.$services.order.getCurrentOrder();
  }

  get showPaymentForm(): boolean {
    return this.$appStore.state.checkout.paymentProvider !== PaymentProviders.Chase &&
      this.$appStore.state.checkout.paymentProvider !== PaymentProviders.UCom &&
      this.$appStore.state.checkout.paymentProvider !== PaymentProviders.ChaseUK;
  }

  get canAddCard(): boolean {
    return !this.$appStore.state.checkout.payment.oneTimePaymentCard;
  }

  get hasError(): boolean {
    return this.isPaymentValid();
  }

  onClickOneTimePayment(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('addCreditOrDebitCardModal', true, { oneTimePaymentUse: true, providerId: this.$appStore.state.checkout.paymentProvider });
    this.$services.analytics.send(new CheckoutEvent('one-time-payment'));
    this.$services.ui.firstFocus();
  }

  isPaymentValid(): boolean {
    const validationResult = this.$appStore.state.customer.validationResult;
    let response = true;

    if (validationResult && validationResult.data) {
      for (const item of validationResult.data) {
        if (!item.valid) {
          response = false;
          break;
        }
      }
    }

    return !response;
  }

  async mounted() {
    this.componentKey = Date.now() + Math.random();

    if (!this.order) {
      throw new Error('Order does not exist at checkout one time payment');
    }

    this.paymentComponent = '';

    try {
      // This component is loaded by cmg-checkout and is protected by a v-if="!isFetchingPaymentProvider"
      // As a result, we cannot load the component until the payment provider is set in state.
      this.providerId = this.$appStore.state.checkout.paymentProvider;

      if (this.providerId) {
        this.paymentComponent = this.getPaymentComponent();
      }
    } catch (error: any) {
      this.$services.logger.error('Could not load payment form', error);
    }
  }

  private getPaymentComponent(): VueConstructor<Vue> {
    switch (this.providerId) {
      case PaymentProviders.Payeezy:
        return CmgPayeezyForm;
      case PaymentProviders.Chase:
        return CmgChaseForm;
      case PaymentProviders.UCom:
        return CmgUComForm;
      case PaymentProviders.ChaseUK:
        return CmgChaseUkForm;
      default:
        throw new Error('No payment form existing for this provider');
    }
  }
}
